import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import { useAutoSave } from '../hooks/useAutoSave';
import {
  atPacketAPI,
  staffAPI,
  providerAPI,
  templateAPI,
  documentAPI,
} from '../services/api';
import { hasPermission } from '../utils/permissions';
import { formatDisplayDate } from '../utils/dateFormat';
import { ageInMonthsDisplay } from '../utils/ageInMonths';
import { validateAtPacketRequired } from '../utils/atPacketValidation';
import { summarizeAtPacketNotificationEvents } from '../utils/atPacketNotificationSummary';
import { mergeDsccStatusLookupForSelect } from '../utils/atPacketDsccStatusOptions';
import { findExternalEmailPreviewFromEvents } from '../utils/atPacketExternalEmailPreview';
import AtPacketExternalEmailPreviewModal from '../components/AtPacketExternalEmailPreviewModal';
import AtPacketProviderOfficeSelect from '../components/AtPacketProviderOfficeSelect';
import { formatAtVendorSiteOptionLabel, formatProviderSiteOptionLabel } from '../utils/providerDisplayLabel';
import DeleteIconButton from '../components/DeleteIconButton';
import EyeIcon from '../components/icons/EyeIcon';
import InfoIconButton from '../components/InfoIconButton';
import SideNavLayout from '../components/SideNavLayout';
import './AtPackets.css';

function fmt(v) {
  if (v == null || v === '') return '—';
  return String(v);
}

function fmtDate(d) {
  if (!d) return '—';
  return formatDisplayDate(d);
}

function fmtName(first, last) {
  return [last, first].filter(Boolean).join(', ') || '—';
}

/** Saved provider+office line: same format as referral PCP / client pediatrician (see formatProviderSiteOptionLabel). */
function atPacketSavedOfficeLabel(packet, kind) {
  if (!packet) return '';
  if (kind === 'at_dr') {
    if (!packet.at_doctor_provider_id) return '';
    const label = formatProviderSiteOptionLabel({
      first_name: packet.at_doctor_first_name,
      last_name: packet.at_doctor_last_name,
      payee_name: packet.at_doctor_payee_name,
      provider_type_code: packet.at_doctor_provider_type_code || 'DR',
      site_street_1: packet.at_doctor_site_street_1,
      site_city: packet.at_doctor_site_city,
      site_label: packet.at_doctor_site_label,
      provider_id: packet.at_doctor_provider_id,
    });
    return label === '—' ? '' : label;
  }
  if (kind === 'requesting') {
    if (!packet.requesting_provider_id) return '';
    const label = formatProviderSiteOptionLabel({
      first_name: packet.requesting_provider_first_name,
      last_name: packet.requesting_provider_last_name,
      payee_name: packet.requesting_provider_payee_name,
      provider_type_code: packet.requesting_provider_type_code,
      site_street_1: packet.requesting_site_street_1,
      site_city: packet.requesting_site_city,
      site_label: packet.requesting_site_label,
      provider_id: packet.requesting_provider_id,
    });
    return label === '—' ? '' : label;
  }
  if (kind === 'ongoing') {
    if (!packet.ongoing_provider_id) return '';
    const label = formatProviderSiteOptionLabel({
      first_name: packet.ongoing_provider_first_name,
      last_name: packet.ongoing_provider_last_name,
      payee_name: packet.ongoing_provider_payee_name,
      provider_type_code: packet.ongoing_provider_type_code,
      site_street_1: packet.ongoing_site_street_1,
      site_city: packet.ongoing_site_city,
      site_label: packet.ongoing_site_label,
      provider_id: packet.ongoing_provider_id,
    });
    return label === '—' ? '' : label;
  }
  if (kind === 'vendor') {
    if (!packet.at_vendor_provider_id) return '';
    const label = formatAtVendorSiteOptionLabel({
      first_name: packet.at_vendor_first_name,
      last_name: packet.at_vendor_last_name,
      payee_name: packet.at_vendor_payee_name,
      provider_type_code: packet.at_vendor_provider_type_code,
      site_street_1: packet.at_vendor_site_street_1,
      site_city: packet.at_vendor_site_city,
      site_label: packet.at_vendor_site_label,
      provider_id: packet.at_vendor_provider_id,
    });
    return label === '—' ? '' : label;
  }
  return '';
}

/** UUID or legacy numeric site id — never parseInt (breaks UUIDs). */
function normalizeAtPacketSiteId(v) {
  if (v == null || v === '') return null;
  const s = String(v).trim();
  if (s === '' || s.startsWith('provider:')) return null;
  return s;
}

/**
 * AT Packet page: form only (no separate view mode). Edit and delete from this page.
 */
function AtPacketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { tenantPath } = useTenantFromUrl();
  const [packet, setPacket] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [externalEmailPreview, setExternalEmailPreview] = useState(null);

  useEffect(() => {
    const summary = location.state?.atPacketNotificationSummary;
    const preview = location.state?.atPacketExternalEmailPreview;
    const nextState = location.state && typeof location.state === 'object' ? { ...location.state } : {};
    let changed = false;
    if (summary) {
      setSuccessMessage(summary);
      delete nextState.atPacketNotificationSummary;
      changed = true;
    }
    if (
      preview
      && preview.packetId != null
      && id != null
      && String(preview.packetId) === String(id)
    ) {
      setExternalEmailPreview(preview);
      delete nextState.atPacketExternalEmailPreview;
      changed = true;
    }
    if (changed) {
      navigate(`${location.pathname}${location.search || ''}`, { replace: true, state: nextState });
    }
  }, [location.state, location.pathname, location.search, navigate, id]);

  // Edit form state (mirrors packet)
  const [form, setForm] = useState({});
  // Lookups for edit
  const [statuses, setStatuses] = useState([]);
  const [staff, setStaff] = useState([]);
  const [insuranceTypes, setInsuranceTypes] = useState([]);
  const [dsccStatuses, setDsccStatuses] = useState([]);
  const [itemTypes, setItemTypes] = useState([]);
  const [itemNames, setItemNames] = useState([]);
  const [showAddItemForm, setShowAddItemForm] = useState(false);
  const [newItemForm, setNewItemForm] = useState({
    item_kind: 'work',
    item_type_id: '',
    item_name_id: '',
    latest_request_date: '',
    received_date: '',
    dates_not_required: false,
    comment: '',
  });
  const [editingItemId, setEditingItemId] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [savingItem, setSavingItem] = useState(false);
  const [addingItem, setAddingItem] = useState(false);
  const [itemError, setItemError] = useState(null);
  const [activeSection, setActiveSection] = useState('packet'); // 'packet' | 'items'
  // AT packet document generation (Packet tab)
  const [atPacketTemplates, setAtPacketTemplates] = useState([]);
  const [atPacketTemplatesLoading, setAtPacketTemplatesLoading] = useState(false);
  const [selectedDocTemplateId, setSelectedDocTemplateId] = useState('');
  const [generatingDoc, setGeneratingDoc] = useState(false);
  const [docGenError, setDocGenError] = useState(null);
  const [generateDocInfoOpen, setGenerateDocInfoOpen] = useState(false);
  const [saveTrigger, setSaveTrigger] = useState(0);

  const loadPacket = async () => {
    if (!id) return;
    try {
      const [res, itemsRes] = await Promise.all([
        atPacketAPI.getById(id),
        atPacketAPI.getItems(id),
      ]);
      const p = res.data?.data ?? null;
      setPacket(p);
      setItems(itemsRes.data?.data ?? []);
      setForm(p ? {
        request_name: p.request_name ?? '',
        primary_language: p.client_primary_language ?? p.primary_language ?? '',
        status_id: p.status_id ?? '',
        sc_staff_id: p.client_assigned_staff_id ?? p.sc_staff_id ?? '',
        at_doctor_provider_id: p.at_doctor_provider_id ?? '',
        at_doctor_provider_site_id: p.at_doctor_provider_site_id != null ? String(p.at_doctor_provider_site_id) : '',
        at_dr_license: p.at_doctor_license ?? p.at_dr_license ?? '',
        requesting_provider_id: p.requesting_provider_id ?? '',
        requesting_provider_site_id: p.requesting_provider_site_id != null ? String(p.requesting_provider_site_id) : '',
        ongoing_provider_id: p.ongoing_provider_id ?? '',
        ongoing_provider_site_id: p.ongoing_provider_site_id != null ? String(p.ongoing_provider_site_id) : '',
        at_vendor_provider_id: p.at_vendor_provider_id ?? '',
        at_vendor_provider_site_id: p.at_vendor_provider_site_id != null ? String(p.at_vendor_provider_site_id) : '',
        insurance_type_id: p.insurance_type_id ?? '',
        dscc_status_id: p.dscc_status_id != null ? String(p.dscc_status_id) : '',
        start_date: p.start_date ? p.start_date.split('T')[0] : '',
        submitted_to_atc_date: p.submitted_to_atc_date ? p.submitted_to_atc_date.split('T')[0] : '',
        approval_date: p.approval_date ? p.approval_date.split('T')[0] : '',
        denied_date: p.denied_date ? p.denied_date.split('T')[0] : '',
        latest_submit_dhs_date: p.latest_submit_dhs_date ? p.latest_submit_dhs_date.split('T')[0] : '',
        comment_log: p.comment_log ?? '',
      } : {});
      setError(null);
    } catch (err) {
      setPacket(null);
      setItems([]);
      setError(err.response?.status === 404 ? 'AT packet not found.' : (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    if (!id) {
      setLoading(false);
      return;
    }
    loadPacket().then(() => {
      if (cancelled) return;
    });
    return () => { cancelled = true; };
  }, [id]);

  // Load AT packet document templates (for Generate document section)
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setAtPacketTemplatesLoading(true);
    templateAPI.getAll({ docType: 'ATPacket' })
      .then((res) => {
        if (cancelled) return;
        const list = res.data?.templates ?? res.data?.data ?? [];
        setAtPacketTemplates(list);
      })
      .catch(() => { if (!cancelled) setAtPacketTemplates([]); })
      .finally(() => { if (!cancelled) setAtPacketTemplatesLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    Promise.all([
      atPacketAPI.getStatuses(),
      staffAPI.getAll({ active: 'true' }),
      atPacketAPI.getInsuranceTypes(),
      atPacketAPI.getDsccStatuses().catch(() => ({ data: { data: [] } })),
      atPacketAPI.getItemTypes(),
      atPacketAPI.getItemNames(),
    ]).then(([sRes, stRes, iRes, dRes, itRes, inRes]) => {
      if (cancelled) return;
      setStatuses(sRes.data?.data ?? []);
      setStaff(stRes.data?.data ?? []);
      setInsuranceTypes(iRes.data?.data ?? []);
      setDsccStatuses(dRes.data?.data ?? []);
      setItemTypes(itRes.data?.data ?? []);
      setItemNames(inRes.data?.data ?? []);
    }).catch(() => {
      if (!cancelled) {
        setStatuses([]);
        setStaff([]);
        setInsuranceTypes([]);
        setDsccStatuses([]);
        setItemTypes([]);
        setItemNames([]);
      }
    });
    return () => { cancelled = true; };
  }, [id]);

  // Derive AT doctor license from selected AT doctor (fetch provider when id changes)
  useEffect(() => {
    if (!form.at_doctor_provider_id) {
      setForm((prev) => (prev.at_dr_license === '' ? prev : { ...prev, at_dr_license: '' }));
      return;
    }
    let cancelled = false;
    providerAPI.getById(form.at_doctor_provider_id)
      .then((res) => {
        if (cancelled) return;
        const doc = res.data?.data;
        const license = doc?.license ?? '';
        setForm((prev) => (prev.at_dr_license === license ? prev : { ...prev, at_dr_license: license }));
        setSaveTrigger((p) => p + 1);
      })
      .catch(() => { if (!cancelled) setForm((prev) => (prev.at_dr_license === '' ? prev : { ...prev, at_dr_license: '' })); });
    return () => { cancelled = true; };
  }, [form.at_doctor_provider_id]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (hasPermission('at_packets.update')) setSaveTrigger((p) => p + 1);
  };

  const dsccStatusSelectOptions = useMemo(
    () => mergeDsccStatusLookupForSelect(dsccStatuses, form.dscc_status_id),
    [dsccStatuses, form.dscc_status_id]
  );

  const patchProviderWithSite = useCallback((providerField, siteField, { providerId, providerSiteId }) => {
    setForm((prev) => ({
      ...prev,
      [providerField]: providerId,
      [siteField]: providerSiteId,
    }));
    if (hasPermission('at_packets.update')) setSaveTrigger((p) => p + 1);
  }, []);

  const performSave = useCallback(async () => {
    setError(null);
    const payload = {
      request_name: form.request_name?.trim() || '',
      primary_language: form.primary_language?.trim() || null,
      status_id: form.status_id || null,
      sc_staff_id: form.sc_staff_id || null,
      at_doctor_provider_id: form.at_doctor_provider_id || null,
      at_doctor_provider_site_id: normalizeAtPacketSiteId(form.at_doctor_provider_site_id),
      at_dr_license: form.at_dr_license?.trim() || null,
      requesting_provider_id: form.requesting_provider_id || null,
      requesting_provider_site_id: normalizeAtPacketSiteId(form.requesting_provider_site_id),
      ongoing_provider_id: form.ongoing_provider_id || null,
      ongoing_provider_site_id: normalizeAtPacketSiteId(form.ongoing_provider_site_id),
      at_vendor_provider_id: form.at_vendor_provider_id || null,
      at_vendor_provider_site_id: normalizeAtPacketSiteId(form.at_vendor_provider_site_id),
      insurance_type_id: form.insurance_type_id || null,
      dscc_status_id: form.dscc_status_id ? parseInt(form.dscc_status_id, 10) : null,
      start_date: form.start_date || null,
      submitted_to_atc_date: form.submitted_to_atc_date || null,
      approval_date: form.approval_date || null,
      denied_date: form.denied_date || null,
      latest_submit_dhs_date: form.latest_submit_dhs_date || null,
      comment_log: form.comment_log?.trim() || null,
    };
    const toValidate = { ...payload, client_id: packet?.client_id };
    const { valid, errors } = validateAtPacketRequired(toValidate, false);
    if (!valid) {
      setSuccessMessage(null);
      setError(`Missing required fields: ${errors.join(', ')}`);
      return;
    }
    const res = await atPacketAPI.update(id, payload);
    setPacket(res.data?.data ?? packet);
    const events = res.data?.notificationEvents || [];
    const previewPayload = findExternalEmailPreviewFromEvents(events);
    if (previewPayload) {
      setExternalEmailPreview({ packetId: id, ...previewPayload });
    } else {
      setExternalEmailPreview(null);
    }
    const note = summarizeAtPacketNotificationEvents(events);
    if (note) {
      setSuccessMessage(note);
    } else {
      const sentTo = res.data?.inboxNotificationSentTo;
      if (sentTo?.name) {
        setSuccessMessage(`Notification sent to ${sentTo.name}'s inbox.`);
      }
    }
    // Do not call loadPacket() here: it would setLoading(true) and overwrite form with server
    // response, causing the same flaky input behavior as on Staff/Tenant Settings.
  }, [form, packet, id]);

  useAutoSave(saveTrigger, performSave, { enabled: !!id && hasPermission('at_packets.update'), delayMs: 500 });

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await performSave();
    } catch (err) {
      setSuccessMessage(null);
      setError(err.response?.data?.error || err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this AT packet? This cannot be undone.')) return;
    setDeleting(true);
    setError(null);
    try {
      await atPacketAPI.delete(id);
      navigate(tenantPath('/lists/at-packets'), { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  const canAddItem = hasPermission('at_packets.create');
  const canUpdateItem = hasPermission('at_packets.update');

  const handleNewItemChange = (field, value) => {
    setNewItemForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    setItemError(null);
    setAddingItem(true);
    try {
      await atPacketAPI.addItem(id, {
        item_kind: newItemForm.item_kind,
        item_type_id: newItemForm.item_type_id || null,
        item_name_id: newItemForm.item_name_id || null,
        latest_request_date: newItemForm.latest_request_date || null,
        received_date: newItemForm.received_date || null,
        dates_not_required: !!newItemForm.dates_not_required,
        comment: newItemForm.comment?.trim() || null,
      });
      setShowAddItemForm(false);
      setNewItemForm({
        item_kind: 'work',
        item_type_id: '',
        item_name_id: '',
        latest_request_date: '',
        received_date: '',
        dates_not_required: false,
        comment: '',
      });
      await loadPacket();
    } catch (err) {
      setItemError(err.response?.data?.error || err.message || 'Failed to add item');
    } finally {
      setAddingItem(false);
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Remove this item from the packet?')) return;
    setItemError(null);
    try {
      await atPacketAPI.deleteItem(id, itemId);
      setEditingItemId(null);
      setEditForm(null);
      await loadPacket();
    } catch (err) {
      setItemError(err.response?.data?.error || err.message || 'Failed to remove item');
    }
  };

  const handleEditItem = (item) => {
    setEditingItemId(item.id);
    setEditForm({
      item_kind: item.item_kind || 'work',
      item_type_id: item.item_type_id || '',
      item_name_id: item.item_name_id || '',
      latest_request_date: item.latest_request_date ? (typeof item.latest_request_date === 'string' ? item.latest_request_date.split('T')[0] : item.latest_request_date) : '',
      received_date: item.received_date ? (typeof item.received_date === 'string' ? item.received_date.split('T')[0] : item.received_date) : '',
      dates_not_required: !!item.dates_not_required,
      comment: item.comment ?? '',
    });
    setItemError(null);
  };

  const handleEditFormChange = (field, value) => {
    setEditForm((prev) => (prev ? { ...prev, [field]: value } : null));
  };

  const handleSaveItem = async (e) => {
    e.preventDefault();
    if (!editingItemId || !editForm) return;
    setItemError(null);
    setSavingItem(true);
    try {
      await atPacketAPI.updateItem(id, editingItemId, {
        item_kind: editForm.item_kind || 'work',
        item_type_id: editForm.item_type_id || null,
        item_name_id: editForm.item_name_id || null,
        latest_request_date: editForm.latest_request_date || null,
        received_date: editForm.received_date || null,
        dates_not_required: !!editForm.dates_not_required,
        comment: editForm.comment?.trim() || null,
      });
      setEditingItemId(null);
      setEditForm(null);
      await loadPacket();
    } catch (err) {
      setItemError(err.response?.data?.error || err.message || 'Failed to save item');
    } finally {
      setSavingItem(false);
    }
  };

  const handleGenerateAtPacketDoc = async () => {
    const templateId = selectedDocTemplateId;
    if (!templateId) return;
    const template = atPacketTemplates.find((t) => t.id === templateId);
    setDocGenError(null);
    setGeneratingDoc(true);
    try {
      const response = await documentAPI.generateForAtPacket(id, templateId);
      const contentType = response.headers['content-type'] || response.headers['Content-Type'] || '';
      if (contentType.includes('application/json')) {
        const errorData = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
        throw new Error(errorData?.error || errorData?.message || 'Failed to generate document');
      }
      const contentDisposition = response.headers['content-disposition'] || response.headers['Content-Disposition'];
      let filename = template ? `${template.displayName}-packet.docx` : `at-packet-document.docx`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (match) filename = match[1].replace(/['"]/g, '');
      }
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setDocGenError(err.response?.data?.error || err.message || 'Failed to generate document');
    } finally {
      setGeneratingDoc(false);
    }
  };

  if (loading) {
    return (
      <div className="at-packets-page">
        <p>Loading AT packet…</p>
      </div>
    );
  }

  if (error && !packet) {
    return (
      <div className="at-packets-page">
        <p className="at-packets-error">{error}</p>
        <p className="at-packets-back">
          <Link to={tenantPath('/lists/at-packets')}>← Back to AT Packets</Link>
        </p>
      </div>
    );
  }

  const clientName = fmtName(packet?.client_first_name, packet?.client_last_name);
  const clientDobAge = packet?.client_dob != null ? ageInMonthsDisplay(packet.client_dob) : null;

  const canUpdate = hasPermission('at_packets.update');

  const nav = (
    <>
      <div className="at-packet-detail-nav-header side-nav-header">
        <h2 className="side-nav-title">AT Packet</h2>
      </div>
      <div className="at-packet-detail-nav-body">
        <button
          type="button"
          className={`side-nav-link at-packet-detail-nav-item ${activeSection === 'packet' ? 'active' : ''}`}
          onClick={() => setActiveSection('packet')}
          aria-current={activeSection === 'packet' ? 'page' : undefined}
        >
          Packet
        </button>
        <button
          type="button"
          className={`side-nav-link at-packet-detail-nav-item ${activeSection === 'items' ? 'active' : ''}`}
          onClick={() => setActiveSection('items')}
          aria-current={activeSection === 'items' ? 'page' : undefined}
        >
          Items{items.length > 0 ? ` (${items.length})` : ''}
        </button>
      </div>
    </>
  );

  return (
    <div className="at-packets-page at-packets-detail-page">
      {(error || successMessage) && (
        <div className="at-packets-detail-alerts" aria-live="polite">
          {error && <p className="at-packets-error">{error}</p>}
          {successMessage && (
            <p className="at-packets-success at-packets-success--multiline">{successMessage}</p>
          )}
        </div>
      )}
      <SideNavLayout nav={nav} navClassName="side-nav-layout-nav--at-packets-detail" isolateCollapse>
        {activeSection === 'packet' && (
        <div className="at-packet-detail-content">
          <div className="at-packets-header at-packets-detail-header">
            <h1 className="at-packet-detail-content-title">Packet</h1>
            {hasPermission('at_packets.delete') && (
              <DeleteIconButton
                className="btn-delete"
                onClick={handleDelete}
                disabled={deleting}
                title={deleting ? 'Deleting…' : 'Delete packet'}
                aria-label={deleting ? 'Deleting…' : 'Delete packet'}
              />
            )}
          </div>
      <form id="at-packet-detail-form" onSubmit={handleSave} className="at-packet-detail-form">
          <fieldset disabled={!hasPermission('at_packets.update')} className="at-packet-detail-fieldset">
          <div className="at-packet-top-row">
            <div className="form-group">
              <label htmlFor="f-request_name">Request name *</label>
              <input
                id="f-request_name"
                type="text"
                value={form.request_name ?? ''}
                onChange={(e) => handleChange('request_name', e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label>Client</label>
              <p className="form-readonly">
                <Link to={tenantPath(`/client/${packet.client_id}`)}>{clientName || '—'}</Link>
              </p>
            </div>
            <div className="form-group">
              <div className="form-group-dob-label-row">
                <label htmlFor="f-client_dob">Date of birth</label>
                {clientDobAge != null && (
                  <span className="form-group-dob-age" aria-live="polite">
                    Age: {clientDobAge}
                  </span>
                )}
              </div>
              <input
                id="f-client_dob"
                type="text"
                className="form-control-readonly"
                value={packet ? fmtDate(packet.client_dob) : ''}
                readOnly
                tabIndex={-1}
                aria-readonly="true"
              />
            </div>
          </div>
          <div className="at-packet-form-grid">
            <div className="form-group">
              <label htmlFor="f-sc_staff_id">Service coordinator (SC)</label>
              <input
                id="f-sc_staff_id"
                type="text"
                className="form-control-readonly"
                value={packet ? fmtName(packet.client_assigned_sc_first_name, packet.client_assigned_sc_last_name) : ''}
                readOnly
                placeholder="From client's assigned SC"
              />
            </div>
            <div className="form-group">
              <label htmlFor="f-primary_language">Primary language</label>
              <input
                id="f-primary_language"
                type="text"
                className="form-control-readonly"
                value={form.primary_language ?? ''}
                readOnly
                placeholder="From client record"
              />
            </div>
            <div className="form-group">
              <label htmlFor="f-client_medicaid_rin">Medicaid RIN</label>
              <input
                id="f-client_medicaid_rin"
                type="text"
                className="form-control-readonly"
                value={packet?.client_medicaid_rin ?? ''}
                readOnly
                tabIndex={-1}
                aria-readonly="true"
                placeholder="From client record"
              />
            </div>
            <div className="form-group">
              <div className="form-label-row">
                <label htmlFor="f-at_doctor_provider_site_id">AT doctor *</label>
                {form.at_doctor_provider_id && (
                  <Link
                    to={tenantPath(`/provider/${form.at_doctor_provider_id}`)}
                    className="form-field-view-icon-link"
                    title="View AT doctor"
                    aria-label="View AT doctor"
                  >
                    <EyeIcon size={18} title="" />
                  </Link>
                )}
              </div>
              <AtPacketProviderOfficeSelect
                id="f-at_doctor_provider_site_id"
                typeCode="DR"
                providerId={form.at_doctor_provider_id ?? ''}
                providerSiteId={form.at_doctor_provider_site_id ?? ''}
                onChange={(v) => patchProviderWithSite('at_doctor_provider_id', 'at_doctor_provider_site_id', v)}
                disabled={!hasPermission('at_packets.update')}
                required
                currentSelectionLabel={atPacketSavedOfficeLabel(packet, 'at_dr')}
              />
            </div>
            <div className="form-group">
              <label htmlFor="f-at_dr_license">AT doctor license</label>
              <input
                id="f-at_dr_license"
                type="text"
                className="form-control-readonly"
                value={form.at_dr_license ?? ''}
                readOnly
                placeholder="From selected AT doctor"
              />
            </div>
            <div className="form-group">
              <label htmlFor="f-status_id">Status *</label>
              <select
                id="f-status_id"
                value={form.status_id ?? ''}
                onChange={(e) => handleChange('status_id', e.target.value)}
              >
                <option value="">—</option>
                {statuses.map((s) => (
                  <option key={s.id} value={s.id}>{s.display_name || s.code}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <div className="form-label-row">
                <label htmlFor="f-requesting_provider_site_id">Requesting provider</label>
                {form.requesting_provider_id && (
                  <Link
                    to={tenantPath(`/provider/${form.requesting_provider_id}`)}
                    className="form-field-view-icon-link"
                    title="View requesting provider"
                    aria-label="View requesting provider"
                  >
                    <EyeIcon size={18} title="" />
                  </Link>
                )}
              </div>
              <AtPacketProviderOfficeSelect
                id="f-requesting_provider_site_id"
                providerId={form.requesting_provider_id ?? ''}
                providerSiteId={form.requesting_provider_site_id ?? ''}
                onChange={(v) => patchProviderWithSite('requesting_provider_id', 'requesting_provider_site_id', v)}
                disabled={!hasPermission('at_packets.update')}
                emptyLabel="Select provider and location…"
                currentSelectionLabel={atPacketSavedOfficeLabel(packet, 'requesting')}
              />
            </div>
            <div className="form-group">
              <div className="form-label-row">
                <label htmlFor="f-ongoing_provider_site_id">Ongoing provider</label>
                {form.ongoing_provider_id && (
                  <Link
                    to={tenantPath(`/provider/${form.ongoing_provider_id}`)}
                    className="form-field-view-icon-link"
                    title="View ongoing provider"
                    aria-label="View ongoing provider"
                  >
                    <EyeIcon size={18} title="" />
                  </Link>
                )}
              </div>
              <AtPacketProviderOfficeSelect
                id="f-ongoing_provider_site_id"
                providerId={form.ongoing_provider_id ?? ''}
                providerSiteId={form.ongoing_provider_site_id ?? ''}
                onChange={(v) => patchProviderWithSite('ongoing_provider_id', 'ongoing_provider_site_id', v)}
                disabled={!hasPermission('at_packets.update')}
                emptyLabel="Select provider and location…"
                currentSelectionLabel={atPacketSavedOfficeLabel(packet, 'ongoing')}
              />
            </div>
            <div className="form-group">
              <div className="form-label-row">
                <label htmlFor="f-at_vendor_provider_site_id">AT vendor</label>
                {form.at_vendor_provider_id && (
                  <Link
                    to={tenantPath(`/provider/${form.at_vendor_provider_id}`)}
                    className="form-field-view-icon-link"
                    title="View AT vendor"
                    aria-label="View AT vendor"
                  >
                    <EyeIcon size={18} title="" />
                  </Link>
                )}
              </div>
              <AtPacketProviderOfficeSelect
                id="f-at_vendor_provider_site_id"
                typeCode="AT"
                formatOptionLabel={formatAtVendorSiteOptionLabel}
                providerId={form.at_vendor_provider_id ?? ''}
                providerSiteId={form.at_vendor_provider_site_id ?? ''}
                onChange={(v) => patchProviderWithSite('at_vendor_provider_id', 'at_vendor_provider_site_id', v)}
                disabled={!hasPermission('at_packets.update')}
                emptyLabel="Select provider and location…"
                searchPlaceholder="Payee name, type AT, or street…"
                resultHint="Search by payee name or type AT — up to 25 vendor offices shown."
                currentSelectionLabel={atPacketSavedOfficeLabel(packet, 'vendor')}
              />
            </div>
            <div className="form-group">
              <div className="form-label-row">
                <label htmlFor={packet?.insurance_type_id ? 'f-insurance_type_id' : undefined}>
                  {packet?.insurance_type_id ? 'Private insurance (legacy)' : 'Insurance'}
                </label>
                {packet?.client_open_referral_id && (
                  <Link
                    to={tenantPath(`/client/${packet.client_id}/referral/${packet.client_open_referral_id}/insurance`)}
                    className="form-field-view-icon-link"
                    title="Edit insurance (referral)"
                    aria-label="Edit insurance (referral)"
                  >
                    <EyeIcon size={18} title="" />
                  </Link>
                )}
              </div>
              {packet?.insurance_type_id ? (
                <select
                  id="f-insurance_type_id"
                  value={form.insurance_type_id ?? ''}
                  onChange={(e) => handleChange('insurance_type_id', e.target.value)}
                >
                  <option value="">—</option>
                  {insuranceTypes.map((i) => (
                    <option key={i.id} value={i.id}>{i.display_name || i.code}</option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  className="form-control-readonly"
                  value="Managed on the referral record"
                  readOnly
                  tabIndex={-1}
                  aria-readonly="true"
                />
              )}
            </div>
            <div className="form-group">
              <label htmlFor="f-dscc_status_id">DSCC status</label>
              <select
                id="f-dscc_status_id"
                value={form.dscc_status_id ?? ''}
                disabled
                title="DSCC status is managed on the client record (read-only here)."
              >
                <option value="">—</option>
                {dsccStatusSelectOptions.map((row) => (
                  <option key={row.id} value={row.id}>{row.display_name}</option>
                ))}
              </select>
              <small style={{ color: '#6c757d' }}>Managed on the client record</small>
            </div>
            <div className="form-group">
              <label htmlFor="f-start_date">Start date *</label>
              <input
                id="f-start_date"
                type="date"
                value={form.start_date ?? ''}
                onChange={(e) => handleChange('start_date', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label htmlFor="f-submitted_to_atc_date">Submitted to ATC date</label>
              <input
                id="f-submitted_to_atc_date"
                type="date"
                value={form.submitted_to_atc_date ?? ''}
                onChange={(e) => handleChange('submitted_to_atc_date', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label htmlFor="f-approval_date">Approval date</label>
              <input
                id="f-approval_date"
                type="date"
                value={form.approval_date ?? ''}
                onChange={(e) => handleChange('approval_date', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label htmlFor="f-denied_date">Denied date</label>
              <input
                id="f-denied_date"
                type="date"
                value={form.denied_date ?? ''}
                onChange={(e) => handleChange('denied_date', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label htmlFor="f-latest_submit_dhs_date">Latest submit DHS date</label>
              <input
                id="f-latest_submit_dhs_date"
                type="date"
                value={form.latest_submit_dhs_date ?? ''}
                onChange={(e) => handleChange('latest_submit_dhs_date', e.target.value)}
              />
            </div>
            <div className="form-group form-group-full">
              <label htmlFor="f-comment_log">Comment log</label>
              <textarea
                id="f-comment_log"
                rows={4}
                value={form.comment_log ?? ''}
                onChange={(e) => handleChange('comment_log', e.target.value)}
              />
            </div>
          </div>

          <section className="at-packet-generate-doc" aria-label="Generate document">
            <div className="at-packet-generate-doc-heading">
              <h4 className="at-packet-generate-doc-title">Generate document</h4>
              <InfoIconButton
                onClick={() => setGenerateDocInfoOpen((v) => !v)}
                ariaExpanded={generateDocInfoOpen}
                ariaControls="at-packet-generate-doc-info"
                ariaLabel="Show explanation"
                ariaLabelExpanded="Hide explanation"
                className="at-packet-generate-doc-info-icon"
              />
            </div>
            {generateDocInfoOpen && (
              <p id="at-packet-generate-doc-info" className="at-packet-generate-doc-intro">
                Choose an AT packet document template and generate a file for this packet.
              </p>
            )}
            {docGenError && <p className="at-packets-error">{docGenError}</p>}
            {atPacketTemplatesLoading ? (
              <p className="at-packet-generate-doc-loading">Loading templates…</p>
            ) : atPacketTemplates.length === 0 ? (
              <p className="at-packet-generate-doc-empty">No AT packet document templates registered yet. Add templates with category &quot;ATPacket&quot; in Settings → Document templates.</p>
            ) : (
              <div className="at-packet-generate-doc-row">
                <select
                  id="at-packet-doc-template"
                  className="at-packet-generate-doc-select"
                  value={selectedDocTemplateId}
                  onChange={(e) => setSelectedDocTemplateId(e.target.value)}
                  aria-label="Select template"
                >
                  <option value="">— Select template —</option>
                  {atPacketTemplates.map((t) => (
                    <option key={t.id} value={t.id}>{t.displayName || t.id}</option>
                  ))}
                </select>
                <button
                  type="button"
                  className="btn-generate-doc"
                  onClick={handleGenerateAtPacketDoc}
                  disabled={!selectedDocTemplateId || generatingDoc}
                >
                  {generatingDoc ? 'Generating…' : 'Generate'}
                </button>
              </div>
            )}
          </section>

          </fieldset>
        </form>
        </div>
        )}
        {activeSection === 'items' && (
        <div className="at-packet-detail-content">
          <div className="at-packets-header at-packets-detail-header">
            <h1 className="at-packet-detail-content-title">Items</h1>
          </div>
          {packet?.client_id && (
            <p className="at-packet-items-client-context">
              <span className="at-packet-items-client-label">Client:</span>{' '}
              <Link to={tenantPath(`/client/${packet.client_id}`)}>{clientName || '—'}</Link>
              {packet.request_name && (
                <><span className="at-packet-items-client-label" style={{ marginLeft: '1.5rem' }}>Request:</span>{' '}{packet.request_name}</>
              )}
            </p>
          )}
        <section className="at-packet-items-section">
          <h3 className="at-packet-items-section-title">Packet items</h3>
          {itemError && <p className="at-packets-error">{itemError}</p>}
          {items.length === 0 && !showAddItemForm && (
            <p className="at-packet-items-empty">
              No items yet.
              {canAddItem && (
                <span className="at-packet-add-item-btn-wrap">
                  <button type="button" className="btn-add-item" onClick={() => setShowAddItemForm(true)}>
                    Add item
                  </button>
                </span>
              )}
            </p>
          )}
          {showAddItemForm && (
            <form className="at-packet-add-item-form" onSubmit={handleAddItem}>
              <h4>Add item</h4>
              <div className="at-packet-add-item-row">
                <div className="form-group">
                  <label htmlFor="new-item_kind">Kind</label>
                  <select
                    id="new-item_kind"
                    value={newItemForm.item_kind}
                    onChange={(e) => {
                      const kind = e.target.value;
                      handleNewItemChange('item_kind', kind);
                      if (kind === 'work') handleNewItemChange('item_name_id', '');
                      if (kind === 'at') handleNewItemChange('item_type_id', '');
                    }}
                  >
                    <option value="work">Work</option>
                    <option value="at">AT</option>
                  </select>
                </div>
                {newItemForm.item_kind === 'work' && (
                <div className="form-group">
                  <label htmlFor="new-item_type_id">Type</label>
                  <select
                    id="new-item_type_id"
                    value={newItemForm.item_type_id}
                    onChange={(e) => handleNewItemChange('item_type_id', e.target.value)}
                  >
                    <option value="">—</option>
                    {itemTypes.map((t) => (
                      <option key={t.id} value={t.id}>{t.display_name || t.code}</option>
                    ))}
                  </select>
                </div>
                )}
                {newItemForm.item_kind === 'at' && (
                <div className="form-group">
                  <label htmlFor="new-item_name_id">Name (from list)</label>
                  <select
                    id="new-item_name_id"
                    value={newItemForm.item_name_id}
                    onChange={(e) => handleNewItemChange('item_name_id', e.target.value)}
                  >
                    <option value="">—</option>
                    {itemNames.map((n) => (
                      <option key={n.id} value={n.id}>{n.display_name || n.code}</option>
                    ))}
                  </select>
                </div>
                )}
                <div className="form-group">
                  <label htmlFor="new-latest_request_date">Requested</label>
                  <input
                    id="new-latest_request_date"
                    type="date"
                    value={newItemForm.latest_request_date}
                    onChange={(e) => handleNewItemChange('latest_request_date', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="new-received_date">Received</label>
                  <input
                    id="new-received_date"
                    type="date"
                    value={newItemForm.received_date}
                    onChange={(e) => handleNewItemChange('received_date', e.target.value)}
                  />
                </div>
                <div className="form-group at-packet-add-item-dates-not-required">
                  <label htmlFor="new-dates_not_required">Dates not required</label>
                  <input
                    id="new-dates_not_required"
                    type="checkbox"
                    checked={!!newItemForm.dates_not_required}
                    onChange={(e) => handleNewItemChange('dates_not_required', e.target.checked)}
                  />
                </div>
              </div>
              <div className="form-group at-packet-add-item-comment">
                <label htmlFor="new-comment">Comment</label>
                <input
                  id="new-comment"
                  type="text"
                  value={newItemForm.comment}
                  onChange={(e) => handleNewItemChange('comment', e.target.value)}
                />
              </div>
              <div className="at-packet-add-item-actions">
                <button type="submit" className="btn-save" disabled={addingItem}>
                  {addingItem ? 'Adding…' : 'Add item'}
                </button>
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => { setShowAddItemForm(false); setItemError(null); }}
                  disabled={addingItem}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
          {editingItemId && editForm && (
            <form className="at-packet-add-item-form at-packet-edit-item-form" onSubmit={handleSaveItem}>
              <div className="at-packet-edit-item-form-header">
                <h4>Edit item</h4>
                {canUpdateItem && (
                  <DeleteIconButton
                    className="btn-delete"
                    onClick={() => handleDeleteItem(editingItemId)}
                    title="Remove item"
                    aria-label="Remove item"
                  />
                )}
              </div>
              <div className="at-packet-edit-item-form-row at-packet-edit-item-form-row--two">
                <div className="form-group">
                  <label htmlFor="edit-item_kind">Kind</label>
                  <select
                    id="edit-item_kind"
                    value={editForm.item_kind}
                    onChange={(e) => handleEditFormChange('item_kind', e.target.value)}
                  >
                    <option value="work">Work</option>
                    <option value="at">AT</option>
                  </select>
                </div>
                {editForm.item_kind === 'work' && (
                  <div className="form-group">
                    <label htmlFor="edit-item_type_id">Type</label>
                    <select
                      id="edit-item_type_id"
                      value={editForm.item_type_id}
                      onChange={(e) => handleEditFormChange('item_type_id', e.target.value)}
                    >
                      <option value="">—</option>
                      {itemTypes.map((t) => (
                        <option key={t.id} value={t.id}>{t.display_name || t.code}</option>
                      ))}
                    </select>
                  </div>
                )}
                {editForm.item_kind === 'at' && (
                  <div className="form-group">
                    <label htmlFor="edit-item_name_id">Name (from list)</label>
                    <select
                      id="edit-item_name_id"
                      value={editForm.item_name_id}
                      onChange={(e) => handleEditFormChange('item_name_id', e.target.value)}
                    >
                      <option value="">—</option>
                      {itemNames.map((n) => (
                        <option key={n.id} value={n.id}>{n.display_name || n.code}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              <div className="at-packet-edit-item-form-row at-packet-edit-item-form-row--two">
                <div className="form-group">
                  <label htmlFor="edit-latest_request_date">Requested</label>
                  <input
                    id="edit-latest_request_date"
                    type="date"
                    value={editForm.latest_request_date}
                    onChange={(e) => handleEditFormChange('latest_request_date', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="edit-received_date">Received</label>
                  <input
                    id="edit-received_date"
                    type="date"
                    value={editForm.received_date}
                    onChange={(e) => handleEditFormChange('received_date', e.target.value)}
                  />
                </div>
              </div>
              <div className="at-packet-edit-item-form-row at-packet-edit-item-form-row--one">
                <div className="form-group at-packet-add-item-dates-not-required">
                  <label htmlFor="edit-dates_not_required">Dates not required</label>
                  <input
                    id="edit-dates_not_required"
                    type="checkbox"
                    checked={!!editForm.dates_not_required}
                    onChange={(e) => handleEditFormChange('dates_not_required', e.target.checked)}
                  />
                </div>
              </div>
              <div className="at-packet-edit-item-form-row at-packet-edit-item-form-row--one">
                <div className="form-group at-packet-add-item-comment">
                  <label htmlFor="edit-comment">Comment</label>
                  <input
                    id="edit-comment"
                    type="text"
                    value={editForm.comment}
                    onChange={(e) => handleEditFormChange('comment', e.target.value)}
                  />
                </div>
              </div>
              <div className="at-packet-add-item-actions">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => { setEditingItemId(null); setEditForm(null); setItemError(null); }}
                  disabled={savingItem}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-save" disabled={savingItem}>
                  {savingItem ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          )}
          {items.length > 0 && (
            <>
              {canAddItem && !showAddItemForm && !editingItemId && (
                <p className="at-packet-items-add-link">
                  <span className="at-packet-add-item-btn-wrap">
                    <button type="button" className="btn-add-item" onClick={() => setShowAddItemForm(true)}>
                      Add item
                    </button>
                  </span>
                </p>
              )}
              <table className="at-packet-items-table">
                <thead>
                  <tr>
                    <th className="at-packet-items-type-col">Type</th>
                    <th>Requested</th>
                    <th>Received</th>
                    <th>Comment</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((i) => (
                    <tr
                      key={i.id}
                      className={canUpdateItem ? 'at-packet-items-row-clickable' : ''}
                      onClick={canUpdateItem ? () => handleEditItem(i) : undefined}
                      role={canUpdateItem ? 'button' : undefined}
                      tabIndex={canUpdateItem ? 0 : undefined}
                      onKeyDown={canUpdateItem ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleEditItem(i); } } : undefined}
                      title={canUpdateItem ? 'Click to edit' : undefined}
                    >
                      <td className="at-packet-items-type-col">{i.item_type_display_name || i.item_type_code || '—'}</td>
                      <td>{fmtDate(i.latest_request_date) || '—'}</td>
                      <td>{fmtDate(i.received_date) || '—'}</td>
                      <td className="at-packet-items-comment-col">{i.comment || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </section>
        </div>
        )}
      </SideNavLayout>
      {externalEmailPreview && (
        <AtPacketExternalEmailPreviewModal
          open
          packetId={externalEmailPreview.packetId}
          subject={externalEmailPreview.subject}
          body={externalEmailPreview.body}
          recipientsPreview={externalEmailPreview.recipientsPreview}
          statusCode={externalEmailPreview.statusCode}
          onDismiss={() => setExternalEmailPreview(null)}
          onSendComplete={() => {
            setSuccessMessage((prev) =>
              [
                prev,
                'If your email program opened, send the message from your mailbox so it stays on your secure account. You can edit the message before sending.',
              ]
                .filter(Boolean)
                .join('\n')
            );
            setExternalEmailPreview(null);
          }}
        />
      )}
    </div>
  );
}

export default AtPacketDetail;
